import React, { useEffect, useState } from "react";
import styles from "./css/MenuButton.module.css";
import { useTranslation } from "react-i18next";
import { langDirection } from "@/utils/languages";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "@/store/store";
import { getEntityName } from "@/utils/getEntityName";

interface MenuButtonProps {
  iconComponent?: React.ElementType;
  iconSrc?: string;
  iconColorType: "fill" | "stroke" | "text";
  className?: string;
  text: string;
  selected: string;
  onClick: (selected: string) => void;
  showText?: boolean;
  isRtl?: boolean;
  id: string;
  shouldOpenSub?: boolean;
  subOptions?: { display: string; navigateTo: string }[];
  selectedSub?: string;
}

export const MenuButton: React.FC<MenuButtonProps> = ({
  iconComponent: IconComponent,
  iconSrc,
  className,
  text,
  selected,
  iconColorType,
  id,
  showText = true,
  onClick,
  shouldOpenSub = false,
  subOptions = [],
  selectedSub = "",
}) => {
  const [hover, setHover] = useState(false);
  const [isSubMenuOpen, setSubMenuOpen] = useState(false);
  const city = useSelector((state: RootState) => state.entity.userCity);
  const ao = useSelector((state: RootState) => state.entity.userAo);
  const entityType = useSelector((state: RootState) => state.entity.entityType);
  const navigate = useNavigate();
  const optionsNames = subOptions.map((option) => option.navigateTo)

  const handleMouseEnter = () => {
    setHover(true);
  };

  const handleMouseLeave = () => {
    setHover(false);
  };

  const { i18n } = useTranslation("LanguageSwitcher");
  const currentLanguage = i18n.language;
  const direction = langDirection(currentLanguage);

  const toggleSubMenu = () => {
    setSubMenuOpen(!isSubMenuOpen);
  };

  useEffect(() => {
    if (selected === id) {
      setSubMenuOpen(true);
    }
  }, [selected]);

  return (
    <>
      <div
        className={`${styles.button} ${styles[direction]} ${className} ${selected === id && !optionsNames.includes(selectedSub) ? styles.selected : ""} ${!showText ? styles.shrink : ""} group`} // Added isRtl class
        onClick={() => {
          onClick(id);
          toggleSubMenu();
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        data-testid={id}
      >
        {direction === "ltr" ? (
          <>
            <div className={styles.iconContainer}>
              {IconComponent ? (
                <IconComponent
                  dir={direction}
                  className={`${styles.icon} ${selected === id && subOptions? styles.selected : ""} ${hover ? styles.hover : ""} ${iconColorType === "fill" ? styles.fillColor : iconColorType === "stroke" ? styles.strokeColor : styles.textColor}`}
                />
              ) : (
                <img
                  src={iconSrc}
                  alt=""
                  className={`${styles.icon} ${selected === id ? styles.selected : ""} ${iconColorType === "fill" ? styles.fillColor : iconColorType === "stroke" ? styles.strokeColor : styles.textColor}`}
                />
              )}
            </div>

            {showText && (
              <div
                className={`${styles.text} ${selected === id ? styles.selected : ""} ${hover ? styles.hover : ""}`}
              >
                {text}
              </div>
            )}
          </>
        ) : (
          <>
            {showText && (
              <div
                className={`${styles.text} ${selected === id ? styles.selected : ""} ${hover ? styles.hover : ""}`}
              >
                {text}
              </div>
            )}
            <div className={styles.iconContainer}>
              {IconComponent ? (
                <IconComponent
                  className={`${styles.icon} ${styles.flipIcon} ${selected === id ? styles.selected : ""} ${hover ? styles.hover : ""} ${iconColorType === "fill" ? styles.fillColor : iconColorType === "stroke" ? styles.strokeColor : styles.textColor}`}
                />
              ) : (
                <img
                  src={iconSrc}
                  alt=""
                  className={`${styles.icon} ${selected === id ? styles.selected : ""} ${iconColorType === "fill" ? styles.fillColor : iconColorType === "stroke" ? styles.strokeColor : styles.textColor}`}
                />
              )}
            </div>
          </>
        )}
      </div>
      {shouldOpenSub && selected === id && (
        <div className={styles.subsContainer}>
          <div
            className={`${styles.subs} ${direction === "rtl" ? styles.rtl : ""} ${isSubMenuOpen ? styles.open : styles.closed}`}
          >
            {subOptions.map((option, index) => (
              <div
                key={index}
                className={styles.sub}
                onClick={() =>
                  navigate(
                    `/${getEntityName(entityType, city, ao)}/${id.toLowerCase()}/${option.navigateTo}`
                  )
                }
                data-testid={`${id}-${option.navigateTo}`}
              >
                <div
                  className={`${styles.subText} ${selectedSub === option.navigateTo ? styles.selected : ""} ${direction === "rtl" ? styles.rtl : ""}`}
                >
                  {option.display}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};
